import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  Ban,
  Check,
  Copy,
  MoreHorizontal,
  Plus,
  Search,
  ShieldCheck,
  ShieldOff,
  Users,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader, PageToolbar } from "@/components/page-header";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { GradientAvatar } from "@/components/ui/gradient-avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { authClient } from "@/lib/auth-client";
import { trpc } from "@/lib/trpc";
import type { AdminUser } from "@/lib/types";
import { cn, relativeTime } from "@/lib/utils";

export const Route = createFileRoute("/_app/admin/users")({
  beforeLoad: ({ context }) => {
    if (context.user.role !== "admin") throw redirect({ to: "/devices" });
  },
  component: AdminUsersPage,
});

type Role = "admin" | "user";

/** Vercel's role picker describes each role under its name; these are ours. */
const ROLES: { value: Role; label: string; description: string }[] = [
  {
    value: "admin",
    label: "Admin",
    description: "Manage providers, users and settings, and join any session",
  },
  { value: "user", label: "User", description: "Reserve devices and control them live" },
];

const roleLabel = (role: string | null | undefined) =>
  ROLES.find((r) => r.value === role)?.label ?? "User";

function AdminUsersPage() {
  const qc = useQueryClient();
  const { user: me } = Route.useRouteContext();
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const { data } = useQuery(trpc.admin.users.queryOptions({ search, limit: 50, offset: 0 }));

  const refresh = () => qc.invalidateQueries({ queryKey: trpc.admin.users.queryKey() });

  // Role and ban changes go through better-auth's admin plugin rather than a
  // tRPC procedure — it owns the session invalidation that must follow them.
  const setRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) =>
      authClient.admin.setRole({ userId, role }),
    onSuccess: () => {
      toast.success("Role updated");
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleBan = useMutation({
    mutationFn: ({ userId, banned }: { userId: string; banned: boolean }) =>
      banned
        ? authClient.admin.unbanUser({ userId })
        : authClient.admin.banUser({ userId, banReason: "Banned by admin" }),
    onSuccess: () => {
      toast.success("Updated");
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const users = data?.users;

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Users"
        meta={<span className="tabular-nums">{data?.total ?? 0} total</span>}
      />

      {/* A settings-style page, so Vercel's Members header: an H1 and its
          description in the content, under the top bar's title. */}
      <div className="mb-8 flex flex-col gap-3">
        <h1 className="text-gray-1000 text-heading-24">Users</h1>
        <p className="text-copy-14 text-gray-900">
          Everyone with an account. Sign-ups are closed, so add people here; promote admins and ban
          accounts from a user's menu.
        </p>
      </div>

      <PageToolbar className="mb-3">
        <Input
          type="search"
          className="h-10 w-full [&>input]:h-full"
          placeholder="Search name or email…"
          aria-label="Search name or email"
          prefix={<Search className="size-4" />}
          prefixStyling={false}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button className="h-10" prefix={<Plus />} onClick={() => setCreating(true)}>
          Add user
        </Button>
      </PageToolbar>

      {users && users.length === 0 ? (
        <EmptyState
          icon={<Users />}
          title={search ? "No users match your search" : "No users yet"}
          description={
            search
              ? "Try a different name or email address."
              : "People appear here once they sign in."
          }
        />
      ) : (
        <ul className="divide-y divide-gray-alpha-400 overflow-hidden rounded-lg bg-background-100 shadow-border">
          {!users &&
            [0, 1, 2].map((i) => (
              <li key={i} aria-hidden className="flex h-[72px] items-center gap-3 px-4">
                <Skeleton shape="circle" className="size-8" />
                <div className="grid gap-2">
                  <Skeleton className="h-3.5 w-28" />
                  <Skeleton className="h-3.5 w-44" />
                </div>
              </li>
            ))}
          {users?.map((u) => {
            const self = u.id === me.id;
            return (
              <li
                key={u.id}
                className="flex items-start gap-3 py-4 pr-2 pl-4 md:items-center md:pr-3"
              >
                <UserAvatar user={u} />
                {/* One element tree for both layouts: on a phone the role and
                    join date wrap under the name; from `md` they dissolve
                    (`contents`) into the row, right-aligned as on Vercel. */}
                <div className="min-w-0 flex-1 md:flex md:items-center md:gap-4">
                  <div className="min-w-0 md:flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate font-medium text-gray-1000 text-label-14">
                        {u.name}
                      </span>
                      {self && (
                        <Badge variant="gray-subtle" size="sm">
                          You
                        </Badge>
                      )}
                      {u.banned && (
                        <Badge variant="red-subtle" size="sm" title={u.banReason ?? undefined}>
                          Banned
                        </Badge>
                      )}
                    </div>
                    <p className="truncate text-copy-14 text-gray-900">{u.email}</p>
                  </div>
                  <div
                    className={cn(
                      "flex flex-wrap items-center gap-y-2 md:contents",
                      self ? "mt-0.5 gap-x-1.5" : "mt-3 gap-x-4",
                    )}
                  >
                    <span
                      className="order-2 whitespace-nowrap text-gray-900 text-label-14 tabular-nums md:order-none"
                      title={new Date(u.createdAt).toLocaleString()}
                    >
                      Joined {relativeTime(u.createdAt)}
                    </span>
                    {/* Your own role is text, as Vercel shows its Owner's:
                        demoting yourself from a select one click away would
                        take this page from you. The menu still offers it. */}
                    {self ? (
                      <span className="text-gray-900 text-label-14 md:flex md:h-8 md:w-28 md:items-center md:justify-end">
                        {roleLabel(u.role)}
                        <span aria-hidden className="md:hidden">
                          {" "}
                          ·
                        </span>
                      </span>
                    ) : (
                      <Select
                        value={u.role === "admin" ? "admin" : "user"}
                        disabled={setRole.isPending}
                        onValueChange={(role) =>
                          setRole.mutate({ userId: u.id, role: role as Role })
                        }
                      >
                        <SelectTrigger
                          size="small"
                          className="w-28"
                          aria-label={`Role for ${u.name}`}
                        >
                          <SelectValue>{roleLabel(u.role)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent align="end" className="w-[26rem] max-w-[calc(100vw-2rem)]">
                          {ROLES.map((r) => (
                            <SelectItem key={r.value} value={r.value} className="h-auto py-2">
                              <span className="flex min-w-0 flex-col items-start">
                                <span className="font-medium text-gray-1000 text-label-14">
                                  {r.label}
                                </span>
                                <span className="text-copy-13 text-gray-900">{r.description}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="tertiary"
                      size="icon-sm"
                      className="-mt-1.5 shrink-0 pointer-coarse:-mt-2.5 pointer-coarse:size-10 md:mt-0 md:pointer-coarse:mt-0"
                      aria-label={`Actions for ${u.name}`}
                    >
                      <MoreHorizontal />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuItem
                      onSelect={() =>
                        setRole.mutate({
                          userId: u.id,
                          role: u.role === "admin" ? "user" : "admin",
                        })
                      }
                    >
                      {u.role === "admin" ? "Demote to user" : "Promote to admin"}
                      {u.role === "admin" ? (
                        <ShieldOff className="ml-auto" />
                      ) : (
                        <ShieldCheck className="ml-auto" />
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant="error"
                      onSelect={() => toggleBan.mutate({ userId: u.id, banned: !!u.banned })}
                    >
                      {u.banned ? "Unban" : "Ban"}
                      <Ban className="ml-auto" />
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            );
          })}
        </ul>
      )}

      <CreateUserDialog open={creating} onOpenChange={setCreating} onCreated={refresh} />
    </div>
  );
}

/** A photo if they have one, otherwise Vercel's gradient disc, seeded so it never changes. */
function UserAvatar({ user }: { user: AdminUser }) {
  return (
    <Avatar>
      {user.image && <AvatarImage src={user.image} alt="" />}
      <AvatarFallback>
        <GradientAvatar seed={user.email} />
      </AvatarFallback>
    </Avatar>
  );
}

/** 18 characters from a CSPRNG, URL-safe so it survives being pasted into a chat. */
function generatePassword() {
  const bytes = crypto.getRandomValues(new Uint8Array(14));
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * Sign-ups are closed by default, so this is how anyone after the first admin
 * gets an account. It goes through better-auth's admin plugin, like role and
 * ban changes, and the coordinator's user hook lets exactly this path through.
 */
function CreateUserDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(generatePassword);
  const [isAdmin, setIsAdmin] = useState(false);
  // Shown once after creation: the password is not stored anywhere readable.
  const [created, setCreated] = useState<{ email: string; password: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const close = (v: boolean) => {
    onOpenChange(v);
    if (v) return;
    setName("");
    setEmail("");
    setPassword(generatePassword());
    setIsAdmin(false);
    setCreated(null);
    setCopied(false);
  };

  const create = useMutation({
    mutationFn: async () => {
      // The client resolves with `error` rather than rejecting.
      const { error } = await authClient.admin.createUser({
        name,
        email,
        password,
        role: isAdmin ? "admin" : "user",
      });
      if (error) throw new Error(error.message ?? "Could not create the user");
    },
    onSuccess: () => {
      setCreated({ email, password });
      onCreated();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add user</DialogTitle>
          <DialogDescription>
            Sign-ups are closed, so accounts are created here. You will see the password once, to
            send to them.
          </DialogDescription>
        </DialogHeader>

        {created ? (
          <div className="flex flex-col gap-4">
            <div className="grid gap-1 rounded-md border bg-muted/40 p-3 font-mono text-sm">
              <span>{created.email}</span>
              <span>{created.password}</span>
            </div>
            <DialogFooter>
              <Button
                variant="secondary"
                prefix={copied ? <Check /> : <Copy />}
                onClick={async () => {
                  await navigator.clipboard.writeText(
                    `Email: ${created.email}\nPassword: ${created.password}`,
                  );
                  setCopied(true);
                }}
              >
                Copy
              </Button>
              <Button onClick={() => close(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <div className="grid gap-1.5">
              <Label htmlFor="u-name">Name</Label>
              <Input id="u-name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="u-email">Email</Label>
              <Input
                id="u-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="u-password">Password</Label>
              <div className="flex gap-2">
                <Input
                  id="u-password"
                  required
                  minLength={8}
                  className="font-mono"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setPassword(generatePassword())}
                >
                  Generate
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="u-admin" checked={isAdmin} onCheckedChange={setIsAdmin} />
              <Label htmlFor="u-admin">Admin</Label>
            </div>

            <DialogFooter>
              <Button type="submit" loading={create.isPending}>
                Create
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
